import type { Publisher } from "@repo/fika-shared/src/loader/publisher-schema.js"
import type { Story } from "@repo/fika-shared/src/loader/story-schema.js"
import type { Subscription } from "@repo/fika-shared/src/loader/subscription-schema.js"
import { format, intlFormatDistance } from "date-fns"
import { createMemo, Show } from "solid-js"
import { useAppContext } from "@/context/app"
import type { SearchResult } from "@/data/search-index"
import { Box } from "@/designSystem/components/box"
import { Text } from "@/designSystem/components/text"
import { extractDomain } from "@/lib/extractDomain"
import { getTitle } from "@/lib/getTitle"
import { generateSearchHighlighter } from "@/lib/searchHighlight"
import { Icon } from "../components/icon"
import { Logo } from "../components/logo"
import { Mark } from "../components/mark"
import { type Pill, Pills } from "./pills"
import { publisherClass, publisherTitleClass } from "./publisher.css"

type Props = {
  publisher: Publisher
  story?: Story | null
  subscription?: Subscription | null
  searchResult?: SearchResult
}
export function PublisherComponent(props: Props) {
  const { db } = useAppContext()
  const bookmark = createMemo(
    () => props.story && db.bookmark.findBy({ story_id: props.story.id }),
  )
  const bookmarkedDate = createMemo(() => bookmark()?.bookmarked_at)
  const subscribedDate = createMemo(() => props.subscription?.created_at)
  const publisherDate = createMemo(() => subscribedDate() || bookmarkedDate())

  const title = createMemo(() => getTitle(props.publisher.dc_title))
  const hl = generateSearchHighlighter(props.searchResult)

  const titleHl = createMemo(() => hl("title", title()))
  const descriptionHl = createMemo(() =>
    hl("description", props.publisher.dc_description),
  )
  const verb = createMemo(() => {
    if (bookmarkedDate()) return " — bookmarked "

    const subscription = props.subscription
    if (!subscription) return null
    if (subscription.silenced) return " — ignored "

    return " — subscribed "
  })

  const getTags = createMemo(() => {
    const target = props.subscription
    if (!target) return []

    return db.tag
      .filterBy({ target_id: target.id, target_type: "subscription" })
      .map(t => t.tag)
  })

  const pills = createMemo(() => {
    const pills: Pill[] = []

    if (props.subscription?.silenced) {
      pills.push({
        icon: "eye-off",
        label: "ignored",
        hue: "emerald",
      })
    } else if (props.subscription) {
      pills.push({
        icon: "plus-circle",
        label: "subscribed",
        hue: "indigo",
      })
    } else if (props.story) {
      const title = getTitle(props.story.dc_title)
      pills.push({
        icon: "telescope",
        label: `discovery — ${title}`,
        hue: "emerald",
      })
    }

    return pills.concat(
      getTags().map(tag => {
        return {
          label: tag,
          rounded: true,
          hue: "sand",
        }
      }),
    )
  })

  return (
    <div class={publisherClass}>
      <Box gap="16" fill>
        <Box gap="8" fill>
          <Box>
            <span class={publisherTitleClass}>
              <Mark hue="indigo" pieces={titleHl()} />
            </span>
            <Text fontSize="s" color="sand800" lineClamp="1">
              {extractDomain(props.publisher.url)}
              <Show when={publisherDate()}>
                {date => (
                  <>
                    {verb()}
                    <Time date={date()} />
                  </>
                )}
              </Show>
            </Text>
          </Box>
          <Show when={descriptionHl().length}>
            <Text fontSize="s" color="sand900" lineClamp="3">
              <Mark hue="indigo" pieces={descriptionHl()} />
            </Text>
          </Show>
        </Box>
        <Pills pills={pills()} />
      </Box>
      <Logo
        image={{ url: props.publisher.logo_url, imageClass: "logo" }}
        fallback={<Icon name="newspaper" color="indigo700" />}
        alt={title()}
      />
    </div>
  )
}

type DateProps = {
  date: string
}
function Time(props: DateProps) {
  const humanReadableDate = createMemo(() => {
    if (!props.date) return null

    return intlFormatDistance(props.date, new Date())
  })

  const formatDate = createMemo(() => {
    if (!props.date) return undefined

    return format(props.date, "PPp")
  })

  return (
    <time title={formatDate()} datetime={formatDate()}>
      {humanReadableDate()}
    </time>
  )
}
